import { createFileRoute } from '@tanstack/react-router';
import { PageContainer } from '@/components/PageContainer';
import { toaster } from '@/components/ui/toaster';
import { trpc } from '@/lib/trpc';
import { Button } from '@televet/kibble-ui/Button';
import { Card } from '@televet/kibble-ui/Card';
import { Alert } from '@televet/kibble-ui/Alert';
import { Flex, Table } from '@televet/kibble-ui/chakra';
import { HStack } from '@televet/kibble-ui/chakra';
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { GenerateAuthLinkModal } from './-components/GenerateAuthLinkModal';
import { CreateTestClinicModal } from './-components/CreateTestClinicModal';
import { ResetMigrationModal } from './-components/ResetMigrationModal';
import { SeedMoreLabDataModal } from './-components/SeedMoreLabDataModal';
import { Icon, TextInput } from '@televet/kibble-ui';
import { Menu } from '@televet/kibble-ui/Menu';
import { Tag } from '@televet/kibble-ui/Tag';
import { isTestClinicCrmId, isTestClinicName } from '@/lib/testClinic';

export const Route = createFileRoute('/_authenticated/clinics/')({
  component: RouteComponent,
  beforeLoad: () => {
    return {
      pageTitle: 'Clinics',
    };
  },
});
function RouteComponent() {
  const [search, setSearch] = useState('');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);
  const isProduction = import.meta.env.VITE_APP_ENV === 'production';

  const {
    data: clinicsData,
    isFetching,
    refetch: refetchClinicsData,
  } = useQuery(
    trpc.clinic.listInformClinics.queryOptions({
      search: search || undefined,
      limit: 20,
      cursor,
    })
  );

  // Reset cursor and history when search changes
  useEffect(() => {
    setCursor(undefined);
    setCursorHistory([]);
  }, [search]);

  const [showGenerateAuthModal, setShowGenerateAuthModal] = useState(false);
  const [showCreateTestClinicModal, setShowCreateTestClinicModal] = useState(false);
  const [editingClinicName, setEditingClinicName] = useState('');
  const [editingClinicId, setEditingClinicId] = useState('');
  const [seedMoreTarget, setSeedMoreTarget] = useState<{ id: string; name: string } | null>(null);
  const [resetTarget, setResetTarget] = useState<{
    clinicName: string;
    clinicId: string;
    crmId: string | null;
    hasFlowSibling: boolean;
  } | null>(null);

  const openResetModal = (target: {
    clinicName: string;
    clinicId: string;
    crmId: string | null;
    hasFlowSibling: boolean;
  }) => {
    // In production, only "[TEST]"-named clinics may be reset (matches the
    // backend guard). The menu already hides this action for other clinics;
    // this is the defense-in-depth check in case it is reached another way.
    if (isProduction && !isTestClinicName(target.clinicName)) {
      toaster.warning({
        title: 'FLOW CLINIC DELETION BLOCKED IN PRODUCTION',
        description: `Unable to reset ${target.clinicName}, as it is a production clinic. Please reach out to a dev to handle this process.`,
      });
      return;
    }
    setResetTarget(target);
  };

  const handleResetModalOpenChange = (open: boolean) => {
    if (!open) {
      setResetTarget(null);
      // Fire-and-forget: the Modal open-change callback is sync; surface
      // unexpected refetch failures via toast instead of an unhandled rejection.
      refetchClinicsData().catch((error) => {
        toaster.error({
          title: 'Failed to refresh clinics',
          description: error instanceof Error ? error.message : 'Unknown error',
        });
      });
    }
  };

  // Pagination
  const handleNext = () => {
    if (clinicsData?.nextCursor) {
      if (cursor) {
        setCursorHistory([...cursorHistory, cursor]);
      }
      setCursor(clinicsData.nextCursor);
    }
  };

  const handlePrevious = () => {
    if (cursorHistory.length > 0) {
      const newHistory = [...cursorHistory];
      const previousCursor = newHistory.pop();
      setCursorHistory(newHistory);
      setCursor(previousCursor);
    } else {
      setCursor(undefined);
    }
  };

  const hasNext = !!clinicsData?.nextCursor;
  const hasPrevious = cursor !== undefined;

  return (
    <PageContainer>
      <Flex direction="column" w="100%" gap={4}>
        <Alert
          title="Admin Feature"
          description="Easily Migrate clinics from Inform to Flow, for testing purposes"
          status="warning"
          // Standing notice about what this page does, not something to dismiss.
          hideCloseButton
        />
        <Flex gap={2} align="center">
          <TextInput
            placeholder="Search by id or name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="lg"
            flex={1}
          />
          <Button
            size="md"
            colorPalette="orange"
            onClick={() => setShowCreateTestClinicModal(true)}
          >
            Create Test Clinic
          </Button>
        </Flex>

        <Card
          flex="1 1 auto"
          direction="column"
          borderColor="border.muted"
          rounded="lg"
          overflow="hidden"
        >
          <Table.Root size="lg">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Inform Clinic ID</Table.ColumnHeader>
                <Table.ColumnHeader>Name</Table.ColumnHeader>
                <Table.ColumnHeader>CRM ID</Table.ColumnHeader>
                <Table.ColumnHeader>Flow Sibling Clinic ID</Table.ColumnHeader>
                <Table.ColumnHeader>Flow Status</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="end">Actions</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {clinicsData?.clinics.map((clinic) => {
                // In production the test tooling (auth link + migration reset)
                // is restricted to "[TEST]"-named clinics, matching the backend
                // guard, so a real clinic can't be acted on by accident. Outside
                // production every clinic is fair game for testing.
                const canUseTestActions = !isProduction || isTestClinicName(clinic.name);

                const menuItems = [
                  ...(canUseTestActions && clinic.crmId
                    ? [
                        {
                          value: 'generate',
                          label: 'Generate Auth Link',
                          leftElement: <Icon name="chainLink" size="sm" />,
                          onSelect: () => {
                            setShowGenerateAuthModal(true);
                            setEditingClinicName(clinic.name);
                            setEditingClinicId(clinic.id);
                          },
                        },
                      ]
                    : []),
                  // Data seeding is wired only outside production.
                  ...(!isProduction && isTestClinicCrmId(clinic.crmId)
                    ? [
                        {
                          value: 'seed-more-lab-data',
                          label: 'Seed More Data',
                          leftElement: <Icon name="plus" size="sm" />,
                          onSelect: () => setSeedMoreTarget({ id: clinic.id, name: clinic.name }),
                        },
                      ]
                    : []),
                  ...(canUseTestActions && clinic.flowId
                    ? [
                        {
                          value: 'reset',
                          label: 'Reset Migration',
                          variant: 'danger' as const,
                          leftElement: <Icon name="trashcan" variant="danger" size="sm" />,
                          onSelect: () =>
                            openResetModal({
                              clinicName: clinic.name,
                              clinicId: clinic.id,
                              crmId: clinic.crmId,
                              hasFlowSibling: !!clinic.flowId,
                            }),
                        },
                      ]
                    : []),
                ];

                return (
                  <Table.Row fontSize="md" key={clinic.id}>
                    <Table.Cell>{clinic.id}</Table.Cell>
                    <Table.Cell>{clinic.name}</Table.Cell>
                    <Table.Cell>{clinic.crmId}</Table.Cell>
                    <Table.Cell>
                      <HStack>{clinic.flowId && <>{clinic.flowId}</>}</HStack>
                    </Table.Cell>
                    <Table.Cell>
                      {clinic.flowId && (
                        <Tag
                          label={clinic.flowIsClinicActive ? 'Active' : 'Inactive'}
                          variant={clinic.flowIsClinicActive ? 'success' : 'error'}
                          size="sm"
                        />
                      )}
                    </Table.Cell>
                    <Table.Cell textAlign="end">
                      {menuItems.length > 0 && (
                        <Menu
                          positioning={{ placement: 'bottom-end' }}
                          buttonProps={{
                            size: 'sm',
                            variant: 'ghostNeutral',
                            iconName: 'dotsVertical',
                            hideCaret: true,
                          }}
                          listProps={{ menuItems }}
                        />
                      )}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table.Root>
        </Card>

        {(hasNext || hasPrevious) && (
          <Flex justify="flex-end" align="center" gap={3} px={2}>
            <Button
              size="sm"
              disabled={!hasPrevious || isFetching}
              onClick={handlePrevious}
              loading={isFetching}
            >
              Previous
            </Button>
            <Button
              size="sm"
              disabled={!hasNext || isFetching}
              onClick={handleNext}
              loading={isFetching}
            >
              Next
            </Button>
          </Flex>
        )}
      </Flex>
      <GenerateAuthLinkModal
        open={showGenerateAuthModal}
        onOpenChange={setShowGenerateAuthModal}
        clinicName={editingClinicName}
        clinicId={editingClinicId}
      />
      <CreateTestClinicModal
        open={showCreateTestClinicModal}
        onOpenChange={setShowCreateTestClinicModal}
      />
      <SeedMoreLabDataModal clinic={seedMoreTarget} onClose={() => setSeedMoreTarget(null)} />
      <ResetMigrationModal
        open={!!resetTarget}
        onOpenChange={handleResetModalOpenChange}
        clinicName={resetTarget?.clinicName ?? ''}
        clinicId={resetTarget?.clinicId ?? ''}
        crmId={resetTarget?.crmId ?? null}
        hasFlowSibling={resetTarget?.hasFlowSibling ?? false}
      />
    </PageContainer>
  );
}
